import TFAContainer from '@nile/components/src/Containers/Login/TFAContainer';
import LoginForm from '@nile/components/src/Forms/LoginForm';
import { Frame, Loading, Banner } from '@shopify/polaris';
import {
  getErrorData,
  getHeaderValue,
  sendMessageToContentJS,
  fetchApi,
  useProduct,
  COMMON_CONSTANTS,
  getIdpHeaders
} from '@nile/components';
import envConfig from '@nile/home/src/Config/env';
import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { SVGIcon } from '@superops/super-icons';
import { FormattedMessage } from 'react-intl';
import { Button, DisplayText, TextStyle } from '@superops/ui-design-system';
import { loggerInfo } from '@nile/home/src/coralogixRUM';

const loginTypes = [
  { name: 'TECHNICIAN', ssoType: 'companyUserSSOStatus' },
  { name: 'REQUESTER', ssoType: 'clientUserSSOStatus' }
];
const LoginPage = (props) => {
  const { screen = 'login', idpFlow = false } = props;
  const supportRoute = screen === 'support';
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passcode, setPasscode] = useState('');
  const [trustme, setTrustme] = useState(false);
  const [loginConfi, setLoginConfi] = useState({});
  const [fetching, setFetching] = useState(false);
  const [showTrustMe, setShowTrustMe] = useState(false);
  const [showUnAuthorizedPage, setShowUnAuthorizedPage] = useState(false);
  const [submitActionLoading, setSubmitActionLoading] = useState(false);
  const currentProductType = useProduct();
  const [serverError, setServerError] = useState(null);
  const [loginStep, setLoginStep] = useState(supportRoute ? 2 : 1); // 1: email, 2: password/others
  const [userLoginConfig, setUserLoginConfig] = useState(null);
  const [showForgotPasswordLink, setShowForgotPasswordLink] = useState(false);

  const pushLogs = (message, errorCode) => {
    // logger?.info('LoginSubscriptionError', {
    //   domain: window.location.origin,
    //   errorCode: errorCode,
    //   errorMessage: message
    // });
    // logger?.flush();

    loggerInfo('LoginSubscriptionError', {
      domain: window.location.origin,
      errorCode: errorCode,
      errorMessage: message
    });
  };

  const fetchLoginConfiguration = async () => {
    const requestOptions = {
      method: 'GET',
      headers: getHeaderValue(),
      credentials: 'include'
    };
    try {
      setFetching(true);
      const response = await fetchApi(
        `${envConfig.loginRoot}/login/accountloginconfiguration`,
        requestOptions
      );
      setFetching(false);
      const data = await response.json();
      let dta = data;
      delete dta.clientUserSSOStatus; // To hide login with client SSO in login page as per new design
      setLoginConfi(dta);
      setShowTrustMe(data?.trustMeStatus);
      if (!response.ok) {
        switch (response.status) {
          case 1027:
            pushLogs(response.status, 'SSO - Email attribute missing');
            break;
          case 1028:
            pushLogs(response.status, 'SSO - Error Validating SAML message');
            break;
          case 563:
            pushLogs(response.status, 'Subscription trial has been expired');
            break;
          case 564:
            pushLogs(response.status, 'Subscription has been paused');
            break;
          case 565:
            pushLogs(response.status, 'Subscription has been cancelled');
            break;
          default:
            break;
        }
      }
    } catch (err) {
      setFetching(false);
      setShowUnAuthorizedPage(true);
    }
  };

  useEffect(() => {
    fetchLoginConfiguration();
  }, []);

  // sending event to extension for logout state
  useEffect(() => {
    localStorage.removeItem('apiToken');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('authorizationtoken');

    // for extension use to focus on domain
    sendMessageToContentJS('authToken');
  }, []);

  const loading = false;

  // New: Handle email submit for step 1
  const handleEmailSubmit = async (email) => {
    setFetching(true);
    setServerError(null);
    setUsername(email);
    try {
      const queryParams = new URLSearchParams({
        email: email,
        deviceType: 'WEB'
      });
      const requestOptions = {
        method: 'GET',
        headers: getIdpHeaders(),
        credentials: 'include'
      };
      const response = await fetchApi(
        `${
          envConfig.accountsBasePath
        }/authorize/user_configuration?${queryParams.toString()}`,
        requestOptions
      );
      if (!response.ok) {
        setShowForgotPasswordLink(true);
        setServerError(getErrorData(response.status));
        setSubmitActionLoading(false);
        setFetching(false);
        return;
      }
      setFetching(false);
      const data = await response.json();
      if (data?.code === 'no_user') {
        setServerError(getErrorData(557));
        setShowForgotPasswordLink(true);
        return;
      }
      if (data?.hasOwnProperty('activatedUser') && !data?.activatedUser) {
        setServerError(getErrorData(560));
        setShowForgotPasswordLink(true);
        return;
      }
      if (data?.authType === 'SSO' && data?.redirectURL) {
        window.location.replace(data?.redirectURL);
        return;
      }
      setUserLoginConfig(data);
      // If SSO is enabled, redirect
      if (
        data?.ssoStatusDto?.ssoEnabled &&
        data?.ssoStatusDto?.ssoRedirectUrl
      ) {
        window.location.replace(data.ssoStatusDto.ssoRedirectUrl);
        // window.open(data?.ssoStatusDto?.ssoRedirectUrl);
        return;
      }
      // Otherwise, show password/other fields
      setLoginStep(2);
      // setShowTrustMe(data?.trustMeStatus);
    } catch (err) {
      setFetching(false);
      setServerError('Unable to fetch login configuration.');
    }
  };

  // Modified LoginUser to use username from state and userLoginConfig
  const LoginUser = async ({ tenantid, username: _username, password }) => {
    const usernameToUse = _username || username;
    setUsername(usernameToUse.trim());
    setPassword(password);
    setTrustme(trustme);
    setServerError(null);
    setSubmitActionLoading(true);

    const formData = new FormData();
    formData.append('username', usernameToUse);
    formData.append('password', password);
    const requestOptions = {
      method: 'POST',
      body: formData,
      headers: getHeaderValue(),
      credentials: 'include'
    };
    const response = await fetchApi(
      `${envConfig.loginRoot}/user/verifycredentials`,
      requestOptions
    );
    if (!response.ok) {
      setServerError(getErrorData(response.status));
      setSubmitActionLoading(false);
      return;
    } else {
      // Use userLoginConfig from the first call
      if (userLoginConfig?.tfaEnabled) {
        setSubmitActionLoading(false);
        props.history.push({
          pathname: '/tfa',
          data: {
            accountId: tenantid,
            username: usernameToUse,
            password,
            trustme
          }
        });
        return;
      }
      // If SSO is enabled, this would have already redirected in step 1
      // Otherwise, proceed with normal login
      try {
        await sendLoginRequest({
          accountId: tenantid,
          username: usernameToUse,
          password,
          trustme,
          ssoEnabled: userLoginConfig?.ssoEnabled
        });
        setSubmitActionLoading(false);
      } catch (err) {
        setSubmitActionLoading(false);
        setServerError('Unable to login.');
      }
    }
  };

  const handleTFACode = (code) => {
    setPasscode(code);
    sendLoginRequest({ passcode: code });
  };

  const sendLoginRequest = async (config) => {
    const _username = config.username || username;
    const _password = config.password || password;
    const _passcode = config.passcode || passcode;
    const _trustme = config.trustme || trustme;

    const formData = new FormData();
    formData.append('username', _username);
    formData.append('password', _password);
    formData.append('passcode', _passcode);

    const requestOptions = {
      method: 'POST',
      body: formData,
      headers: getHeaderValue(),
      credentials: 'include'
    };

    try {
      const response = await fetchApi(
        `${envConfig.loginRoot}/login?trustdurationenable=${_trustme}`,
        requestOptions
      );
      if (response.status === 200) {
        // document.cookie = 'uitoken=' + shortid.generate() + '-' + shortid.generate() + '-' + shortid.generate();
        window.location.reload();
      } else if (!response.ok) {
        setServerError(getErrorData(response.status));
        setSubmitActionLoading(false);
        return;
      }
    } catch (err) {
      new Error(err);
    }
  };

  const handleTrustMeChange = (value) => {
    setTrustme(value);
  };

  if (loading)
    return (
      <Frame>
        <Loading />
      </Frame>
    );

  if (props.screenType === 'tfaconfirmation') {
    return <TFAContainer handleTFACode={handleTFACode} />;
  }

  const displayLoginForm = () => {
    //IT product will either have only login form or SSO
    if (supportRoute) {
      return true;
    }
    if (
      currentProductType === COMMON_CONSTANTS.IT &&
      loginConfi?.companyUserSSOStatus?.ssoEnabled
    )
      return false;
    return (
      !loginConfi?.companyUserSSOStatus?.ssoEnabled ||
      !loginConfi?.clientUserSSOStatus?.ssoEnabled
    );
  };

  const showSSobutton = (loginType, ssoRedirectUrl) => {
    return (
      <div
        className="flex jctr border p10 br-5 cp"
        onClick={() => window.location.replace(ssoRedirectUrl)}
      >
        <div className="flex f11 g10 actr">
          {currentProductType === 'IT' &&
          loginConfi?.companyUserSSOStatus?.ssoEnabled
            ? '👤'
            : loginType === 'TECHNICIAN'
            ? '🔧'
            : '👤'}
          <TextStyle weight="Medium">
            <FormattedMessage
              id={
                loginType === 'TECHNICIAN'
                  ? currentProductType === COMMON_CONSTANTS.IT
                    ? 'common.user'
                    : 'app.common.technician'
                  : 'common.requester'
              }
            />
          </TextStyle>
        </div>
        <div className="flex actr jend">
          <SVGIcon name="ArrowRight" />
        </div>
      </div>
    );
  };

  // Modified showLoginForm to support step 1 and 2
  const showLoginForm = () => {
    if (showUnAuthorizedPage) {
      return (
        <div className="mt-20">
          <Banner
            title={<FormattedMessage id="app.auth.domain.cors.title" />}
            status="critical"
          >
            <p>
              <FormattedMessage id="app.auth.domain.cors.description" />
            </p>
          </Banner>
        </div>
      );
    }
    // Step 1: Only email field
    if (loginStep === 1) {
      return (
        <LoginForm
          step={1}
          fetching={fetching}
          serverError={serverError}
          onEmailSubmit={handleEmailSubmit}
          idpFlow={idpFlow}
          showForgotPasswordLink={showForgotPasswordLink}
          {...props}
        />
      );
    }
    // Step 2: Show password/other fields
    return (
      <LoginForm
        step={2}
        supportRoute={supportRoute}
        LoginUser={LoginUser}
        fetching={fetching}
        trustme={trustme}
        showTrustMe={showTrustMe}
        idpFlow={idpFlow}
        handleTrustMeChange={handleTrustMeChange}
        serverError={serverError}
        submitActionLoading={submitActionLoading}
        username={username}
        {...props}
      />
    );
  };
  return (
    <>
      {displayLoginForm() && (
        <div className="position-relative">{showLoginForm()}</div>
      )}
      {(loginConfi?.companyUserSSOStatus?.ssoEnabled ||
        loginConfi?.clientUserSSOStatus?.ssoEnabled) &&
        !idpFlow &&
        !supportRoute && (
          <div className="flex fcol mt40 g20">
            <div className="flex ">
              <DisplayText element="h2" weight="SemiBold">
                <FormattedMessage id="app.auth.ssoButton.login" />
              </DisplayText>
            </div>
            {loginTypes.map((item) => {
              return (
                loginConfi[item.ssoType]?.ssoEnabled &&
                showSSobutton(
                  item.name,
                  loginConfi[item.ssoType]?.ssoRedirectUrl
                )
              );
            })}
          </div>
        )}
    </>
  );
};

export default withRouter(LoginPage);
